import React from "react";
import ReactDOMServer from "react-dom/server";
import errorService from "./errorService";
import VideoDownloadPage from "../../components/common/VideoDownloadPage";
import ImageDownloadPage from "../../components/common/ImageDownloadPage";

interface DownloadMediaFileOptions {
  url: string;
  mediaType: "image" | "video";
  filename?: string;
  extension?: string;
  organization?: {
    name: string;
  };
}

const MEDIA_EXTENSION_BY_MIME: Record<string, string> = {
  "video/mp4": "mp4",
  "video/webm": "webm",
  "video/quicktime": "mov",
  "video/x-msvideo": "avi",
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "image/gif": "gif"
};

const getExtensionFromMimeType = (mimeType = ""): string | undefined => (
  MEDIA_EXTENSION_BY_MIME[mimeType.split(";")[0].trim().toLowerCase()]
);

const getExtensionFromUrl = (url: string): string | undefined => {
  try {
    const parsedUrl = new URL(url, window.location.origin);
    const pathname = parsedUrl.pathname || "";
    const extension = pathname.split(".").pop();

    if (extension && extension.length <= 5) return extension.toLowerCase();
    return undefined;
  } catch {
    return undefined;
  }
};

const getDefaultExtension = (
  mediaType: "image" | "video",
  {
    customExtension,
    contentType,
    blobType,
    url
  }: {
    customExtension?: string;
    contentType?: string;
    blobType?: string;
    url: string;
  }
): string => (
  customExtension
  || getExtensionFromMimeType(contentType)
  || getExtensionFromMimeType(blobType)
  || getExtensionFromUrl(url)
  || (mediaType === "image" ? "jpg" : "mp4")
);

const buildDefaultFilename = (mediaType: "image" | "video", extension: string): string => (
  `${Date.now()}_${mediaType === "image" ? "photo" : "video"}.${extension}`
);

const createVideoDownloadPage = (videoUrl: string): string => ReactDOMServer.renderToString(
  <VideoDownloadPage videoUrl={videoUrl} />
);

const createImageDownloadPage = (imageUrl: string): string => ReactDOMServer.renderToString(
  <ImageDownloadPage imageUrl={imageUrl} />
);

const downloadMediaFile = async ({
  url,
  mediaType,
  filename,
  extension,
  organization
}: DownloadMediaFileOptions): Promise<void> => {
  try {
    if (organization?.name === "Lloyds Banking Group") {
      const directDownloadExtension = getDefaultExtension(mediaType, {
        customExtension: extension,
        url
      });
      const directDownloadFilename = filename
        || buildDefaultFilename(mediaType, directDownloadExtension);
      const link = document.createElement("a");
      link.href = url;
      link.download = directDownloadFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    // For other organizations, normal download
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Failed to download media. Status: ${response.status}`);

    const contentType = response.headers.get("content-type") || "";
    const expectedTypePrefix = mediaType === "video" ? "video/" : "image/";
    const validContentType = contentType === ""
      || contentType.startsWith(expectedTypePrefix)
      || contentType.includes("application/octet-stream");
    if (!validContentType) {
      throw new Error(`Invalid content type for ${mediaType}: ${contentType}`);
    }

    const blob = await response.blob();
    if (blob.size === 0) throw new Error("Empty file downloaded");

    const resolvedExtension = getDefaultExtension(mediaType, {
      customExtension: extension,
      contentType,
      blobType: blob.type,
      url
    });
    const resolvedFilename = filename || buildDefaultFilename(mediaType, resolvedExtension);

    const downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = resolvedFilename;
    document.body.appendChild(link);
    link.click();

    setTimeout(() => {
      URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(link);
    }, 200);
  } catch (error) {
    // Special logic for Lloyds Banking Group - open in new tab in case of error
    if (organization?.name === "Lloyds Banking Group") {
      try {
        // Create HTML content based on media type
        const htmlContent = mediaType === "video"
          ? createVideoDownloadPage(url)
          : createImageDownloadPage(url);

        const blob = new Blob([htmlContent], { type: "text/html" });
        const htmlUrl = URL.createObjectURL(blob);

        // Use only window.open, no fallbacks to prevent double tabs
        const newWindow = window.open(htmlUrl, "_blank", "noopener,noreferrer");
        if (newWindow) {
          // Successfully opened in new window
          setTimeout(() => URL.revokeObjectURL(htmlUrl), 5000);
        } else {
          // If window.open fails, just revoke the URL and don't try alternatives
          URL.revokeObjectURL(htmlUrl);
        }
        return;
      } catch (linkError) {
        // Final fallback: use window.open
        window.open(url, "_blank", "noopener,noreferrer");
        return;
      }
    }

    errorService.error(error);
  }
};

const downloadFile = async (
  url: string,
  mediaType: "image" | "video",
  organization?: { name: string }
): Promise<void> => downloadMediaFile({ url, mediaType, organization });

export { downloadMediaFile, downloadFile };
export default downloadMediaFile;
